import React from 'react';
import { createPortal } from 'react-dom';
import { Crown, Plus, Search, Trash2, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { CommanServices, normalizeIncidentRecipientDirectory } from '@/services/commanServices';
import { loadAssignUsers } from './AssignPersonnelModal';

const ruleFor = (id, index) => ({
  escalated_to: String(id), escalation_trigger: 'Verification', response_time: 'Immediate',
  response_time_custom: null, order_index: index, auto_trigger_action_card_id: null, is_active: true,
});

const TeamStaffModal = ({ team, incidentId = null, isAdmin = false, onClose }) => {
  const [tab, setTab] = React.useState('contact');
  const [teamDoc, setTeamDoc] = React.useState(team);
  const [users, setUsers] = React.useState([]);
  const [crewId, setCrewId] = React.useState(team?.crews?.[0]?.id || '');
  const [query, setQuery] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const reload = React.useCallback(async () => {
    if (!isAdmin && incidentId) {
      const response = await CommanServices.getIncidentRecipientDirectory(incidentId);
      const directory = normalizeIncidentRecipientDirectory(response);
      const current = directory.teams.find((row) => String(row.id || row._id) === String(team.id)) || team;
      setTeamDoc(current);
      setUsers(directory.users);
      if (!crewId && current?.crews?.[0]?.id) setCrewId(current.crews[0].id);
      return;
    }
    const [teamsRes, { raw }] = await Promise.all([CommanServices.getTeamsData(), loadAssignUsers()]);
    const teams = Array.isArray(teamsRes?.data) ? teamsRes.data : [];
    const current = teams.find((row) => String(row.id) === String(team.id)) || team;
    setTeamDoc(current);
    setUsers(Array.isArray(raw) ? raw : []);
    if (!crewId && current?.crews?.[0]?.id) setCrewId(current.crews[0].id);
  }, [crewId, incidentId, isAdmin, team]);

  React.useEffect(() => { reload(); }, [reload]);

  const resolveUser = React.useCallback((ref) => {
    const value = String(ref || '').toLowerCase();
    return users.find((u) => [u.id, u.email, u.user_account].some((key) => String(key || '').toLowerCase() === value));
  }, [users]);

  const members = React.useMemo(() => {
    const map = new Map();
    (teamDoc?.crews || []).forEach((crew) => {
      users.forEach((user) => {
        if (user.is_active !== false && String(user.crew_id || '') === String(crew.id)) {
          map.set(String(user.id), { user, crews: new Set([crew.crew_name]) });
        }
      });
      (crew.escalation_rules || []).forEach((rule) => {
        if (rule?.is_active === false) return;
        const refs = Array.isArray(rule.escalated_to) ? rule.escalated_to : [rule.escalated_to];
        refs.forEach((ref) => {
          const user = resolveUser(ref); if (!user) return;
          const id = String(user.id);
          if (!map.has(id)) map.set(id, { user, crews: new Set() });
          map.get(id).crews.add(crew.crew_name);
        });
      });
    });
    const leaders = new Set((teamDoc?.leaders || []).map(String));
    return [...map.values()].map(({ user, crews }) => ({
      id: String(user.id), name: user.name || user.full_name || user.email || 'User',
      email: user.email || '', crews: [...crews].filter(Boolean), isLeader: leaders.has(String(user.id)),
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [teamDoc, users, resolveUser]);

  const toggleLeader = async (member) => {
    setBusy(true);
    try {
      const leaders = new Set((teamDoc.leaders || []).map(String));
      if (leaders.has(member.id)) leaders.delete(member.id); else leaders.add(member.id);
      const res = await CommanServices.updateteams(teamDoc.id, { leaders: [...leaders] });
      if (res?.status === false) throw new Error(res.message || 'Failed');
      toast.success(`${member.name} ${leaders.has(member.id) ? 'is now' : 'is no longer'} a team leader`);
      await reload();
    } catch (error) { toast.error(error?.message || 'Failed to update team leader'); }
    finally { setBusy(false); }
  };

  const addMember = async (user) => {
    const crew = (teamDoc.crews || []).find((row) => String(row.id) === String(crewId));
    if (!crew) return toast.error('Choose a crew first');
    setBusy(true);
    try {
      const rules = (crew.escalation_rules || []).map((rule, index) => ({ ...rule, order_index: index }));
      rules.push(ruleFor(user.id, rules.length));
      const res = await CommanServices.updateTeamCrew(teamDoc.id, crew.id, { escalation_rules: rules });
      if (res?.status === false) throw new Error(res.message || 'Failed');
      toast.success(`${user.name || user.email} added to ${crew.crew_name}`);
      setQuery(''); await reload();
    } catch (error) { toast.error(error?.message || 'Failed to add member'); }
    finally { setBusy(false); }
  };

  const memberIds = new Set(members.map((member) => member.id));
  const q = query.trim().toLowerCase();
  const addable = users.filter((user) => user.is_active !== false && !memberIds.has(String(user.id)) && (!q || `${user.name || ''} ${user.email || ''}`.toLowerCase().includes(q))).slice(0, 30);

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" data-testid="team-staff-modal">
      <div className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between bg-gradient-to-r from-slate-800 to-indigo-900 px-6 py-4 text-white">
          <div><h2 className="text-lg font-bold">{teamDoc?.team_name}</h2><p className="text-xs text-indigo-200">Team staff and leadership</p></div>
          <Button variant="ghost" size="sm" onClick={onClose} className="rounded-full text-white/70 hover:bg-white/10 hover:text-white"><X className="h-4 w-4" /></Button>
        </div>
        <div className="flex border-b px-6">
          {['contact', ...(isAdmin ? ['manage'] : [])].map((name) => <button key={name} onClick={() => setTab(name)} className={`border-b-2 px-4 py-2.5 text-sm font-semibold ${tab === name ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500'}`}>{name === 'contact' ? 'Staff & Contact' : 'Manage Members'}</button>)}
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto p-6">
          {members.map((member) => (
            <div key={member.id} className="flex items-center gap-3 rounded-xl border p-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-600 text-sm font-bold text-white">{member.name[0]?.toUpperCase()}</span>
              <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold">{member.name}</p><p className="truncate text-[11px] text-gray-500">{member.email}{member.crews.length ? ` · ${member.crews.join(', ')}` : ''}</p></div>
              {tab === 'contact' && member.isLeader && <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-[10px] font-bold uppercase text-amber-700"><Crown className="h-3 w-3" /> Leader</span>}
              {tab === 'manage' && <button role="switch" aria-checked={member.isLeader} disabled={busy} onClick={() => toggleLeader(member)} className={`inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-[10px] font-bold uppercase ${member.isLeader ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-500'}`}><Crown className="h-3 w-3" />{member.isLeader ? 'Leader' : 'Make leader'}</button>}
            </div>
          ))}
          {!members.length && <p className="py-6 text-center text-sm text-gray-400">No members in this team yet.</p>}
          {tab === 'manage' && (
            <div className="mt-5 rounded-xl border bg-slate-50 p-4">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500">Add member to a crew</p>
              <div className="grid gap-2 sm:grid-cols-[180px_1fr]">
                <select value={crewId} onChange={(e) => setCrewId(e.target.value)} className="rounded-md border bg-white px-2 py-2 text-sm"><option value="">Choose crew…</option>{(teamDoc?.crews || []).map((crew) => <option key={crew.id} value={crew.id}>{crew.crew_name}</option>)}</select>
                <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search users…" className="w-full rounded-md border bg-white py-2 pl-8 pr-2 text-sm" /></div>
              </div>
              {!!query && <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border bg-white">{addable.map((user) => <button key={user.id} onClick={() => addMember(user)} disabled={busy || !crewId} className="flex w-full items-center gap-2 border-b px-3 py-2 text-left text-sm hover:bg-indigo-50 disabled:opacity-50"><Plus className="h-3.5 w-3.5 text-indigo-500" />{user.name || user.email}</button>)}</div>}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between border-t px-6 py-3"><span className="text-xs text-gray-400"><Users className="mr-1 inline h-3 w-3" />{members.length} members</span><Button size="sm" onClick={onClose}>Done</Button></div>
      </div>
    </div>, document.body
  );
};

export default TeamStaffModal;
